import { LineupStatus } from '@uefadigital/panenka';
import { PkDivider, PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useGetLineUpQuery } from 'api/fspApi';
import { LineUps } from 'components/LineUps';
import { PreviousLineUps } from 'components/PreviousLineUps';
import { Substitutes } from 'components/Substitutes';
import { Substitutions } from 'components/Substitutions';
import useIsDesktop from 'hooks/useIsDesktop';
import { useMatchData } from 'hooks/useMatchData';
import type { LineUps as LineUp } from 'types/ApiResponseInterfaces';

import * as style from './style.module.css';

const fieldSizeDefault = {
  desktop: 81.5,
  mobile: 100,
  desktopJerseySize: 32,
  mobileJerseySize: 28,
};

const prevLineUpsFieldSizeDefault = {
  desktop: 93.5,
  mobile: 100,
  desktopJerseySize: 32,
  mobileJerseySize: 28,
};

export const LineUpsTab = ({
  fieldSize = fieldSizeDefault,
  prevLineUpsFieldSize = prevLineUpsFieldSizeDefault,
  isPDFPrint = false,
}) => {
  const { matchData, competition } = useMatchData();

  const isDesktop = useIsDesktop();
  const { t } = useTranslation();
  const Title = isDesktop
    ? 'pk-font-size--xl2 pk-font-weight--bolder pk-line-height--sd pk-font-style--normal pk-text--text-01'
    : 'pk-font-size--xl pk-font-weight--bolder pk-line-height--sd pk-font-style--normal pk-text--text-01';

  const { data: lineUps = {} as LineUp } = useGetLineUpQuery(matchData.id);

  const hasLineUpsPositions =
    lineUps?.homeTeam?.field[0]?.fieldCoordinate &&
    lineUps?.homeTeam?.field[0]?.fieldCoordinate.x !== 0 &&
    lineUps?.homeTeam?.field[0]?.fieldCoordinate.y !== 0 &&
    lineUps?.awayTeam?.field[0]?.fieldCoordinate &&
    lineUps?.awayTeam?.field[0]?.fieldCoordinate.x !== 0 &&
    lineUps?.awayTeam?.field[0]?.fieldCoordinate.y !== 0;

  const SubstitutesComponent = (
    <div className={Title}>
      <Substitutes
        matchData={matchData}
        competition={competition}
        isDesktop={isDesktop}
        noPosition={!hasLineUpsPositions}
      />
    </div>
  );
  const LineUpsComponent = (
    <div className={Title}>
      <LineUps
        noPosition={!hasLineUpsPositions}
        lineUps={lineUps}
        competition={competition}
        match={matchData}
        isDesktop={isDesktop}
        fieldSize={fieldSize}
      />
    </div>
  );
  const SubstitutionsComponent = (
    <Substitutions competition={competition} matchData={matchData} isDesktop={isDesktop} isPDFPrint={isPDFPrint} />
  );
  const PreviousLineUpsComponent = (
    <PreviousLineUps
      competition={competition}
      matchData={matchData}
      fieldSize={prevLineUpsFieldSize}
      isPDFPrint={isPDFPrint}
    />
  );
  const isAvailable = matchData.lineupStatus === LineupStatus.TACTICAL_AVAILABLE;

  const LineUpsNotAvailable = (
    <div
      className={`${isDesktop ? 'pk-font-size--l pk-font-weight--normal' : 'pk-font-size--s pk-font-weight--light'}`}
    >
      <PkIcon name="spectator-stadium" className="pk-d--inline-block pk-align--middle" />{' '}
      <span className="pk-text--text-02">{t('official_line_ups_not_yet_available')}</span>
    </div>
  );
  return (
    <div className={clsx(!isPDFPrint && 'pk-container pk-pt--l pk-pt-md--xl2')}>
      {isDesktop || isPDFPrint ? (
        <>
          {isAvailable && (
            <div className="pk-col pk-col--span-12 pk-bg--elevation-01 pr-page-break--before">
              <div className={clsx('pk-p--m pk-p-md--l pk-d--flex pk-flex--wrap pk-gap--m')}>
                <div
                  className={clsx({
                    [style.flex5]: !isPDFPrint && hasLineUpsPositions,
                    [style.flex7]: !isPDFPrint && !hasLineUpsPositions,
                    'flex-1': isPDFPrint,
                  })}
                >
                  {LineUpsComponent}
                </div>
                <div className={clsx(isPDFPrint ? 'flex-1' : style.flex7)}>{SubstitutesComponent}</div>
              </div>
              <div className="pk-px--m pk-pt--xl2 pk-pb--xs2 pr-display--none">
                <PkDivider class="divider--horizontal pk-px--auto" direction="horizontal" />
              </div>
              <div className="pk-p--l pr-page-break--before">{SubstitutionsComponent}</div>
              {!isPDFPrint && (
                <div className="pr-page-break--before pk-px--l pk-pt--xl pr-pt--0">{PreviousLineUpsComponent}</div>
              )}
            </div>
          )}
          {!isAvailable && (
            <div
              className="pk-col pk-col--span-12 pk-justify-content--center pk-d--flex pk-m--auto pk-bg--elevation-01 pk-px--l pk-pt--xl"
              style={{ height: '324px' }}
            >
              {LineUpsNotAvailable}
            </div>
          )}
        </>
      ) : (
        <>
          {isAvailable && (
            <div className="pk-col pk-col--span-12 pk-bg--elevation-01 pk-px--0 pk-pt--m">{LineUpsComponent}</div>
          )}
          {isAvailable && (
            <div className="pk-col pk-col--span-12 pk-bg--elevation-01 pk-px--m pk-pt--xl">
              {SubstitutionsComponent}
            </div>
          )}
          {isAvailable && (
            <div className="pk-col pk-col--span-12 pk-bg--elevation-01 pk-px--m pk-pt--xl">{SubstitutesComponent}</div>
          )}
          {!isAvailable && (
            <div
              className="pk-col pk-col--span-12 pk-bg--elevation-01 pk-px--m pk-pt--xl pk-justify-content--center pk-d--flex pk-m--auto"
              style={{ height: '324px' }}
            >
              {LineUpsNotAvailable}
            </div>
          )}
          <div
            className={`${isDesktop ? 'pk-px--m' : 'pk-pl--m'} pk-col pk-col--span-12 pk-bg--elevation-01 pk-pt--xl`}
          >
            {PreviousLineUpsComponent}
          </div>
        </>
      )}
    </div>
  );
};
